import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import classNames from 'classnames';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import Avatar from '../../../components/Avatar';
import Button from '../../../components/bootstrap/Button';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../../../components/bootstrap/Modal';
import { editProfile, getProfile } from '../../../services/adminProfile';
import Input from '../../../components/bootstrap/forms/Input';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import useDarkMode from '../../../hooks/useDarkMode';
import ProfileImage from '../../../assets/img/wanna/wanna3.webp'
import AuthContext from '../../../contexts/authContext';

interface ProfileData {
    fName: string;
    lName: string;
    email: string
}

interface ProfileDataEdit {
    fName: string;
    lName: string;
    password: string;
}

const Profile = () => {
    const { themeStatus, darkModeStatus } = useDarkMode();
    const { id } = useParams();
    const [profileData, setProfileData] = useState<ProfileData | null>(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const { setUser } = useContext(AuthContext);

    const fetchProfileData = async () => {
        try {
            const response = await getProfile();
            if (response?.data) {
                setProfileData(response.data);
                if (setUser) {
                    setUser(response.data.fName, response.data);
                }
            } else {
                toast.error('Failed to fetch profile data');
            }
        } catch (error) {
            console.error('Error while fetching profile data:', error);
            toast.error('Error while fetching profile data');
        }
    };

    const formik = useFormik({
        initialValues: {
            fName: profileData?.fName || '',
            lName: profileData?.lName || '',
            password: '',
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            fName: Yup.string().required('First name is required'),
            lName: Yup.string().required('Last name is required'),
            password: Yup.string()
                .min(6, 'Password must be at least 6 characters')
                .required('Password is required')
                .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
                .matches(/[!@#$%^&*]/, 'Password must contain at least one special character'),
        }),
        onSubmit: async (values, formikHelpers) => {
            try {
                const response = await editProfile(values);
                if (!response?.error) {
                    toast.success('Profile updated successfully');
                    setEditModalOpen(false);
                    fetchProfileData()
                    formikHelpers.resetForm()
                } else {
                    toast.error('Failed to update profile');
                }
            } catch (error) {
                toast.error('Error while updating profile');
                console.log(error)
            }
        },
    });

    useEffect(() => {
        fetchProfileData();
    }, []);


    return (
        <>
            {profileData && (
                <PageWrapper title={`${profileData.fName} ${profileData.lName}`}>
                    <Page container='fluid'>
                        <div className="row px-4">
                            <div className="col-lg-6">
                                <Card className="shadow-3d-info">
                                    <CardBody>
                                        <div className="row g-5">
                                            <div className="col-12 pt-3 pb-5 d-flex align-items-center justify-content-between">
                                                <span className="display-4 fw-bold me-3">
                                                    {`${profileData.fName} ${profileData.lName}`}
                                                </span>

                                                <Button
                                                    isOutline={!darkModeStatus}
                                                    color='dark'
                                                    isLight={darkModeStatus}
                                                    className={classNames('text-nowrap', {
                                                        'border-light': !darkModeStatus,
                                                    })}
                                                    icon='Edit'
                                                    onClick={() => setEditModalOpen(true)}>
                                                    Edit
                                                </Button>
                                            </div>
                                            {/* <div className="col-12 d-flex align-items-center justify-content-center">
                                                <Avatar src={ProfileImage} />
                                            </div> */}
                                            <div className="col-12">
                                                <div className="row g-2">
                                                    <div className="col-12 text-center">
                                                        <div className="text-muted">Email Address</div>
                                                        <div className="fw-bold fs-5">
                                                            {profileData.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>
                        </div>
                    </Page>
                </PageWrapper>
            )}

            {/* Edit Profile Modal */}
            <Modal
                setIsOpen={setEditModalOpen}
                isOpen={editModalOpen}
                titleId="editProfile"
                isCentered
                isScrollable
                size="lg"
            >
                <ModalHeader setIsOpen={setEditModalOpen}>
                    <h5 id="editProfile">Edit Profile</h5>
                </ModalHeader>
                <form onSubmit={formik.handleSubmit}>
                    <ModalBody>
                        <div className="row g-4">
                            <div className="col-6">
                                <FormGroup id="fName" label="First Name">
                                    <Input
                                        name="fName"
                                        placeholder="First Name"
                                        onChange={formik.handleChange}
                                        value={formik.values.fName}
                                        onBlur={formik.handleBlur}
                                        isTouched={formik.touched.fName}
                                        invalidFeedback={formik.errors.fName}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-6">
                                <FormGroup id="lName" label="Last Name">
                                    <Input
                                        name="lName"
                                        placeholder="Last Name"
                                        onChange={formik.handleChange}
                                        value={formik.values.lName}
                                        onBlur={formik.handleBlur}
                                        isTouched={formik.touched.lName}
                                        invalidFeedback={formik.errors.lName}
                                    />
                                </FormGroup>
                            </div>
                            <div className="col-12">
                                <FormGroup id="password" label="New Password">
                                    <Input
                                        id="password"
                                        name="password"
                                        type="password"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur} // Important for tracking touched state
                                        value={formik.values.password}
                                        isTouched={formik.touched.password}
                                        invalidFeedback={formik.errors.password}
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter className="bg-transparent">
                        <Button color="info" className="w-100" type="submit">
                            Save Changes
                        </Button>
                    </ModalFooter>
                </form>
            </Modal>
        </>
    );
};

export default Profile;